import { DateRangePicker } from './DateRangePicker'
import { ArrowCounterClockwise, SlidersHorizontal } from '@/design-system/icons'
import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { ApiError } from '@/api/client'
import { type DateRange } from '@/domain/dates'
import { formatNumber, useT } from '@/i18n'
import { useUi } from '@/stores/ui'
import { SearchInput } from './forms'
import { ErrorCard, type ErrorDetail } from './ErrorCard'
import { useActivePage } from '../pageContext'
import { iconForLabel } from '../labelIcons'
import { useCountUp } from '../motion'
import { readError } from '../errorReason'
import { blockedAssignDetails } from '../errorDetails'
import { playAlertSound } from '../sound'
import { Badge, Button, Card, cn, Icon, Skeleton, type IconComponent, type Tone } from './primitives'

/* ------------------------------------------------------------ PageHeader */

export function PageHeader({ title, description, actions, meta, className, icon }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; meta?: ReactNode; className?: string; icon?: IconComponent | null }) {
  const active = useActivePage()
  const PageIcon = icon === null ? undefined : (icon ?? active?.icon)
  return (
    <div className={cn('flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between', className)}>
      <div className="flex min-w-0 items-start gap-3.5">
        {PageIcon && (
          <span className="mt-0.5 flex size-10 shrink-0 animate-pop items-center justify-center rounded-[12px] sm:size-12 sm:rounded-[14px] border border-border bg-surface text-brand-text shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
            <Icon icon={PageIcon} size={24} weight="duotone" />
          </span>
        )}
        <div className="min-w-0">
        <h1 className="title-in flex items-center gap-2 text-xl font-bold tracking-tight sm:text-[28px] sm:leading-9">{title}</h1>
        {description && <p className="desc-in mt-1 max-w-3xl text-[13px] leading-relaxed text-fg-muted sm:text-sm">{description}</p>}
        {meta && <div className="desc-in mt-2 flex flex-wrap items-center gap-2">{meta}</div>}
        </div>
      </div>
      {actions && <div className="desc-in flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

/** Standard page frame: consistent padding, max width and vertical rhythm for every screen — the header, then each block, enters in turn. */
export function Page({ children, className, full }: { children: ReactNode; className?: string; full?: boolean }) {
  return <div className={cn('page-stagger mx-auto flex w-full flex-col gap-4 px-3 py-4 sm:gap-6 sm:px-8 sm:py-8', full ? 'h-full min-h-0 max-w-none' : 'max-w-[1600px]', className)}>{children}</div>
}

export function Section({ title, description, actions, children, className }: { title?: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('flex flex-col gap-3', className)}>
      {(title || actions) && (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            {title && <h2 className="text-base font-bold text-fg-strong">{title}</h2>}
            {description && <p className="text-xs text-fg-muted">{description}</p>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  )
}

/* -------------------------------------------------------------- KpiCard */

const KPI_TILE: Record<Tone, string> = {
  neutral: 'bg-neutral-soft text-fg-muted', brand: 'bg-brand-soft text-brand-text', accent: 'bg-accent-soft text-accent', info: 'bg-info-soft text-info',
  success: 'bg-success/10 text-success', warning: 'bg-warning/10 text-warning', danger: 'bg-danger/10 text-danger',
}

export function KpiCard({ label, value, icon, tone = 'neutral', delta, hint, loading, onClick }: { label: ReactNode; value: ReactNode; icon?: IconComponent; tone?: Tone; delta?: number | null; hint?: ReactNode; loading?: boolean; onClick?: () => void }) {
  const { locale } = useT()
  const Comp = onClick ? 'button' : 'div'
  const animated = useCountUp(typeof value === 'number' ? value : null)
  /*
   * The premium card (owner, 2026-10-30, reference pictures: «كروت … فيها الارقام … مش عجباني»): the label with its icon
   * on a tile tinted by the card's tone, the number large with its change beside it as a pill, the explanation last.
   */
  return (
    <Card className={cn('group min-w-0 px-4 py-4 text-start sm:px-5', onClick && 'cursor-pointer transition-[transform,box-shadow,border-color] hover:-translate-y-0.5 hover:border-border-strong hover:shadow-[0_16px_32px_-18px_rgba(16,24,40,0.28)]')}>
      <Comp onClick={onClick} className="block w-full text-start">
        <p className="flex items-center gap-2.5 text-[12.5px] font-semibold text-fg-muted sm:text-[13px]" title={typeof label === 'string' ? label : undefined}>
          {icon && <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-xl', KPI_TILE[tone])}><Icon icon={icon} size={18} weight="duotone" /></span>}
          <span className="line-clamp-2 min-w-0">{label}</span>
        </p>
        <div className="mt-3 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1.5">
          {loading ? <Skeleton className="h-8 w-28" /> : <p className="tabular min-w-0 break-words text-[24px] font-extrabold leading-none tracking-[-0.6px] text-fg-strong sm:text-[28px]">{typeof value === 'number' ? formatNumber(Math.round(animated), locale) : (value ?? '—')}</p>}
          {!loading && delta != null && (
            <span className={cn('tabular inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-bold', delta >= 0 ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger')}>{delta >= 0 ? '▲' : '▼'} {formatNumber(Math.abs(delta), locale, { maximumFractionDigits: 1 })}%</span>
          )}
        </div>
        {hint && <div className="mt-2 text-[12px] leading-relaxed text-fg-muted">{hint}</div>}
      </Comp>
    </Card>
  )
}

/* ----------------------------------------------------------------- Tabs */

export function Tabs<K extends string>({ value, onChange, items, className }: { value: K; onChange: (k: K) => void; items: { key: K; label: ReactNode; count?: number; hidden?: boolean }[]; className?: string }) {
  return (
    // the portal's tabs (owner, 2026-10-20): the open one in red with a red line under it
    <div role="tablist" className={cn('flex min-w-0 shrink-0 gap-2 overflow-x-auto overflow-y-hidden border-b border-border [scrollbar-width:none]', className)}>
      {items
        .filter((i) => !i.hidden)
        .map((i) => (
          <button
            key={i.key}
            role="tab"
            aria-selected={value === i.key}
            onClick={() => onChange(i.key)}
            className={cn(
              '-mb-px inline-flex h-11 shrink-0 items-center gap-1.5 border-b-2 px-3 text-[15px] transition-colors',
              value === i.key ? 'border-brand font-bold text-brand-text' : 'border-transparent font-medium text-fg-muted hover:text-fg',
            )}
          >
            {i.label}
            {i.count != null && <Badge tone={value === i.key ? 'brand' : 'neutral'}>{i.count}</Badge>}
          </button>
        ))}
    </div>
  )
}

/* ------------------------------------------------------------- FilterBar */

/** The period filter everywhere: the calendar picker with quick choices (DateRangePicker). */
export function DateRangeFilter({ value, onChange }: { value: DateRange; onChange: (r: DateRange) => void }) {
  return <DateRangePicker value={value} onChange={onChange} />
}

/** Filter card: search on the start, labelled filters, reset on the end (reference layout). */
export function FilterBar({ search, onSearch, searchPlaceholder, children, advanced, activeCount = 0, onReset, className }: { search?: string; onSearch?: (v: string) => void; searchPlaceholder?: string; children?: ReactNode; advanced?: ReactNode; activeCount?: number; onReset?: () => void; className?: string }) {
  const { t } = useT()
  const [open, setOpen] = useState(false)
  return (
    <div className={cn('flex flex-col gap-3 rounded-[14px] border border-border bg-surface p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)]', className)}>
      <div className="flex flex-wrap items-end gap-3">
        {onSearch && <SearchInput value={search ?? ''} onChange={onSearch} placeholder={searchPlaceholder ?? t('common.searchPlaceholder')} className="h-10 w-80 max-w-full text-sm" />}
        {children}
        <div className="ms-auto flex items-center gap-2">
          {advanced && (
            <Button variant={open ? 'primary' : 'secondary'} icon={SlidersHorizontal} onClick={() => setOpen((o) => !o)}>
              {t('common.filters')}
              {activeCount > 0 && <Badge tone="accent">{activeCount}</Badge>}
            </Button>
          )}
          {onReset && (
            <Button variant="ghost" icon={ArrowCounterClockwise} onClick={onReset} disabled={activeCount === 0}>
              {t('common.reset')}
            </Button>
          )}
        </div>
      </div>
      {advanced && open && <div className="grid animate-fade-in gap-3 border-t border-border pt-3 sm:grid-cols-2 lg:grid-cols-4">{advanced}</div>}
    </div>
  )
}

/**
 * Small label above a filter control. `plain` wraps it in a div instead of a label — for controls that are
 * themselves buttons or popovers (a calendar), where a label would swallow or double the click.
 */
export function FilterField({ label, icon, children, plain }: { label: ReactNode; icon?: IconComponent; children: ReactNode; plain?: boolean }) {
  const LabelIcon = icon ?? iconForLabel(label)
  const Tag = plain ? 'div' : 'label'
  return (
    <Tag className="flex w-full min-w-0 flex-col gap-1.5 sm:w-auto">
      <span className="flex items-center gap-1.5 px-0.5 text-[12.5px] font-semibold text-fg-muted">
        <Icon icon={LabelIcon} size={13} className="text-fg-subtle" />
        <span>{label}</span>
      </span>
      {children}
    </Tag>
  )
}

/* ---------------------------------------------------------------- Toast */

/** Extra context for an error about one entity — the shipment that cannot be assigned, and how to try again. */
export interface NotifyErrorOptions {
  /** Shown instead of a guessed headline. */
  title?: string
  /** The labelled facts grid — build it with `shipmentErrorDetails` / `runnerErrorDetails`. */
  details?: ErrorDetail[]
  onRetry?: () => void
}

export const notify = {
  success: (message: string, description?: string) => toast.success(message, { description }),
  info: (message: string, description?: string) => toast(message, { description }),
  /**
   * Every surfaced error, in one card: what failed, why in plain Arabic, the facts about the thing it is about,
   * and the technical detail folded away for support (`readError` + `ErrorCard`). Plays the alert sound and stays
   * on screen (top-center, must be dismissed) instead of the quiet corner toast used for success/info — an
   * Operations screen left unattended must not let a failed action pass silently.
   *
   * Wired globally in `app/queryClient.ts` for every query and mutation failure, so any failed read or write
   * (assign to runner, create transaction, ...) gets this with no per-call wiring. Pass `options` where the call
   * site knows more than the HTTP error does.
   */
  error: (error: unknown, fallback?: string, options: NotifyErrorOptions = {}) => {
    const reading = readError(error, fallback)
    // A refusal from the gateway's assign guard already carries the shipment's facts — lay them out with no
    // wiring at the call site, so every screen that assigns gets the full card for free.
    const details = options.details ?? (error instanceof ApiError ? blockedAssignDetails(error.details) : undefined)
    playAlertSound({ enabled: useUi.getState().errorSound })
    toast.custom(
      (id) => (
        <ErrorCard
          title={options.title ?? reading.title}
          reason={reading.reason}
          hint={reading.hint}
          details={details}
          technical={reading.technical}
          onRetry={options.onRetry ? () => { toast.dismiss(id); options.onRetry!() } : undefined}
          onDismiss={() => toast.dismiss(id)}
        />
      ),
      { position: 'top-center', duration: 12_000 },
    )
  },
}

/* ------------------------------------------------------- DescriptionList */

export function DescriptionList({ items, columns = 2 }: { items: { label: ReactNode; value: ReactNode; hidden?: boolean; mono?: boolean }[]; columns?: 1 | 2 | 3 }) {
  return (
    <dl className={cn('grid gap-x-6 gap-y-3', columns === 2 && 'sm:grid-cols-2', columns === 3 && 'sm:grid-cols-2 lg:grid-cols-3')}>
      {items
        .filter((i) => !i.hidden)
        .map((i, idx) => (
          <div key={idx} className="min-w-0">
            <dt className="text-xs text-fg-muted">{i.label}</dt>
            <dd className={cn('mt-0.5 break-words text-sm', i.mono && 'font-mono text-xs')}>{i.value === null || i.value === undefined || i.value === '' ? <span className="text-fg-subtle">—</span> : i.value}</dd>
          </div>
        ))}
    </dl>
  )
}
